import {
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  Notifications,
} from "@mui/icons-material";
import { Badge, Box, Button, ListItem, Menu, Typography } from "@mui/material";

import Helpers from "../../../helpers";
import { translate } from "../../translate/translate";
import { useNotification } from "./useNotification";

export const NotificationButton = () => {
  const {
    notifications,
    badgeContent,
    anchorElNotification,
    openNotificationMenu,
    getBorderColor,
    getButtonLabel,
    getUserNameLabel,
    getNotificationTitle,
    shouldShowActionButton,
    shouldShowRemainingTime,
    formatRemainingTime,
    getNotificationDescription,
    handleOpenNotificationMenu,
    handleCloseNotificationsMenu,
    handleClickNotification,
  } = useNotification();

  return (
    <>
      {/* Botão de notificação */}
      <Button
        id="notification-button"
        color="inherit"
        sx={{ marginRight: 2 }}
        onClick={handleOpenNotificationMenu}
      >
        <Badge color="secondary" badgeContent={badgeContent}>
          <Notifications />
        </Badge>
      </Button>

      {/* Menu de notificações */}
      <Menu
        id="notification-menu"
        aria-labelledby="notification-button"
        anchorEl={anchorElNotification}
        open={openNotificationMenu}
        onClose={handleCloseNotificationsMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: "540px",
              minWidth: "300px",
              maxWidth: "400px",
              width: "auto",
              padding: 0,
              overflowY: "auto",
              whiteSpace: "normal",
              "::-webkit-scrollbar": {
                width: "0px",
              },
              "::-webkit-scrollbar-thumb": {
                background: "transparent",
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            },
          },
        }}
      >
        {/* Cabeçalho do menu */}
        <Box
          sx={{
            backgroundColor: "primary.main",
            width: "100%",
            p: 2,
            position: "sticky",
            top: 0,
            zIndex: 1,
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle2" sx={{ color: "white", fontWeight: 600 }}>
            {translate.t("notifications")}
          </Typography>
        </Box>

        {/* Lista de notificações ou estado vazio */}
        {notifications.length > 0 ? (
          notifications.map((notification, index) => (
            <Box key={notification.id || index} sx={{ px: 0, mb: 0 }}>
              <ListItem
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  borderRadius: 0,
                  overflow: "hidden",
                  backgroundColor: "background.paper",
                  borderBottom: 1,
                  borderColor: "grey.200",
                  width: "auto",
                  whiteSpace: "normal",
                  borderLeft: getBorderColor(notification),
                  px: 2,
                  py: 1.5,
                }}
              >
                <Box sx={{ width: "100%" }}>
                  {/* Título da notificação */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: 12,
                      color: "text.primary",
                      mb: 0.5,
                    }}
                  >
                    {getNotificationTitle(notification)}
                  </Typography>

                  {/* Descrição */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      mb: 0.5,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "pre-wrap",
                      maxWidth: "360px",
                    }}
                  >
                    {getNotificationDescription(notification)}
                  </Typography>

                  {/* Tempo restante (apenas para processamento) */}
                  {shouldShowRemainingTime(notification) && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {translate.t("time_remaining")}
                      </Typography>

                      <AccessTimeIcon
                        sx={{
                          fontSize: 16,
                          color: "secondary.main",
                          verticalAlign: "middle",
                        }}
                      />

                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatRemainingTime(notification)}
                      </Typography>
                    </Box>
                  )}

                  {/* Informações de data/hora e ação */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      mt: 1,
                      flexWrap: "nowrap",
                    }}
                  >
                    {/* Informações de data e hora */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flexShrink: 1,
                      }}
                    >
                      <CalendarTodayIcon sx={{ fontSize: 16, color: "secondary.main" }} />
                      <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 600 }}>
                        {Helpers.formatDate(notification.created_at, "dd/MM/yyyy")}
                      </Typography>

                      <AccessTimeIcon sx={{ fontSize: 16, color: "secondary.main" }} />
                      <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 600 }}>
                        {Helpers.formatDate(notification.created_at, "HH:mm")}h
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          ml: 1.2,
                          display: "inline-block",
                          mr: 5,
                        }}
                      >
                        {getUserNameLabel(notification)}
                      </Typography>
                    </Box>

                    {/* Botão de ação */}
                    {shouldShowActionButton(notification) && (
                      <Button
                        variant="text"
                        size="small"
                        sx={{
                          textTransform: "none",
                          fontSize: "12px",
                          color: "secondary.main",
                          fontWeight: 600,
                          p: 0,
                          minWidth: "auto",
                          ml: 1,
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                        }}
                        onClick={e => handleClickNotification(e, notification)}
                      >
                        {getButtonLabel(notification)}
                      </Button>
                    )}
                  </Box>
                </Box>
              </ListItem>
            </Box>
          ))
        ) : (
          <Typography sx={{ p: 2 }} variant="body2" color="text.secondary">
            {translate.t("no_notifications")}
          </Typography>
        )}
      </Menu>
    </>
  );
};
